import React, { useState } from "react";
import { ReceiptLong } from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  CircularProgress,
  Tooltip,
  Box,
} from "@mui/material";
import { toast } from "react-toastify";
import { useUnit } from "effector-react";
import { Colors } from "../../../../constants/Colors";
import { Fleet } from "../../../../types/fleets.types";
import { Invoice, InvoiceStatus } from "../../../../types/invoices.types";
import {
  getInvoicesByFleetEffect,
  payInvoiceEffect,
} from "../../../../stores/InvoicesStore/InvoicesStore.effects";
import { formatIsoToBrDate, toReais } from "../../../../utils/functions";

interface Props {
  fleet: Fleet;
}

const STATUS_CONFIG: Record<
  InvoiceStatus,
  { label: string; color: "success" | "warning" | "default" }
> = {
  PENDING: { label: "Pendente", color: "warning" },
  PAID: { label: "Pago", color: "success" },
  CANCELLED: { label: "Cancelada", color: "default" },
};

const ViewInvoices: React.FC<Props> = ({ fleet }) => {
  const [open, setOpen] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [payingId, setPayingId] = useState<string | null>(null);

  const loading = useUnit(getInvoicesByFleetEffect.pending);
  const paying = useUnit(payInvoiceEffect.pending);

  const loadInvoices = () => {
    getInvoicesByFleetEffect(fleet.id)
      .then(setInvoices)
      .catch(() => toast.error("Erro ao buscar faturas!"));
  };

  const handleOpen = () => {
    setOpen(true);
    loadInvoices();
  };

  const handlePay = (invoice: Invoice) => {
    setPayingId(invoice.id);
    payInvoiceEffect(invoice.id)
      .then((updated) => {
        toast.success("Pagamento confirmado!");
        setInvoices((prev) =>
          prev.map((inv) =>
            inv.id === invoice.id ? { ...inv, ...updated, status: "PAID" } : inv
          )
        );
      })
      .catch(() => toast.error("Erro ao confirmar pagamento!"))
      .finally(() => setPayingId(null));
  };

  const renderBody = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={7} align="center">
            <CircularProgress size={24} />
          </TableCell>
        </TableRow>
      );
    }

    if (!invoices.length) {
      return (
        <TableRow>
          <TableCell colSpan={7}>Sem faturas para exibir.</TableCell>
        </TableRow>
      );
    }

    return invoices.map((invoice) => {
      const isPending = invoice.status === "PENDING";
      const statusConfig = STATUS_CONFIG[invoice.status] ?? STATUS_CONFIG.PENDING;
      return (
        <TableRow key={invoice.id} hover>
          <TableCell>{fleet.client?.name ?? "—"}</TableCell>
          <TableCell>{fleet.client?.document ?? "—"}</TableCell>
          <TableCell>{toReais(invoice.amount)}</TableCell>
          <TableCell>{formatIsoToBrDate(invoice.periodEndDate)}</TableCell>
          <TableCell>{formatIsoToBrDate(invoice.dueDate)}</TableCell>
          <TableCell>
            <Chip size="small" label={statusConfig.label} color={statusConfig.color} />
          </TableCell>
          <TableCell align="right">
            <Button
              size="small"
              variant="outlined"
              disabled={!isPending || paying}
              onClick={() => handlePay(invoice)}
            >
              {paying && payingId === invoice.id ? (
                <CircularProgress size={16} />
              ) : (
                "Confirmar pagamento"
              )}
            </Button>
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <>
      <Tooltip title="Ver faturas" placement="top">
        <ReceiptLong
          sx={{
            color: Colors.primary[100],
            cursor: "pointer",
            ":hover": { opacity: "70%" },
          }}
          onClick={handleOpen}
        />
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Faturas — {fleet.description}</DialogTitle>
        <DialogContent>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Cliente</TableCell>
                  <TableCell>CNPJ</TableCell>
                  <TableCell>Valor</TableCell>
                  <TableCell>Dia de fechamento</TableCell>
                  <TableCell>Dia de faturamento</TableCell>
                  <TableCell>Pago</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{renderBody()}</TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ViewInvoices;
